/**
 * SOV Event Handler Pattern Generators
 *
 * Generates event handler patterns for Subject-Object-Verb languages:
 * Japanese, Korean, Turkish, Bengali, Quechua, Hindi.
 *
 * Extracted from pattern-generator.ts for maintainability.
 */

import type { LanguagePattern, PatternToken, ExtractionRule } from '../types';
import type { LanguageProfile, KeywordTranslation, RoleMarker } from './language-profiles';
import type { CommandSchema, RoleSpec } from './command-schemas';
import {
  eventHandlerDestinationExtraction,
  eventHandlerDestinationGroup,
  eventHandlerSourceExtraction,
  eventHandlerSourceGroup,
} from './command-schemas';
import type { GeneratorConfig } from './pattern-generator';
import { schemaMarkerAlternatives } from '../parser/utils/marker-resolution';

/**
 * Resolve the surface marker (and any allomorph alternatives) for a role in a
 * given language. Prefers the schema's per-command `markerOverride`, else the
 * profile's default roleMarker; in BOTH cases the schema's declared alternatives
 * (`markerLegacy` ∪ `markerVariants`) merge in via `schemaMarkerAlternatives`,
 * so the generated pattern matches every surface form the i18n transformer can
 * produce.
 *
 * Why: `markerOverride` is a single string, so it silently dropped the allomorph
 * alternatives the profile roleMarker would otherwise have supplied. The Turkish
 * dative the transformer emits under vowel harmony (e/a/ye/ya) is the canonical
 * case: tr set-attribute's `… doğru ya ayarla` (value=true marked with the -ya
 * dative) failed to match the `e`-only patient marker, so the whole set fell to
 * the role-scrambling generic SOV extraction. See STRUCTURAL_ARCS_ROADMAP.md.
 *
 * This used to be the only generator that merged variants in both branches,
 * which is why tr `set` worked here and nowhere else.
 */
function resolveRoleMarker(
  roleSpec: RoleSpec,
  profile: LanguageProfile
): { marker: string | undefined; alternatives: string[] | undefined } {
  let marker: string | undefined;
  let alternatives: string[] | undefined;

  if (roleSpec.markerOverride && roleSpec.markerOverride[profile.code] !== undefined) {
    marker = roleSpec.markerOverride[profile.code];
    // An override replaces the profile's alternatives, so the markers a previous
    // release rendered would stop parsing without markerLegacy.
    alternatives = marker ? schemaMarkerAlternatives(roleSpec, profile.code, marker) : undefined;
  } else {
    const roleMarker = profile.roleMarkers[roleSpec.role];
    if (roleMarker) {
      marker = roleMarker.primary;
      const schemaAlts = schemaMarkerAlternatives(roleSpec, profile.code, marker) ?? [];
      const merged = [...new Set([...(roleMarker.alternatives ?? []), ...schemaAlts])].filter(
        a => a !== marker
      );
      alternatives = merged.length ? merged : undefined;
    }
  }

  return { marker, alternatives };
}

/**
 * Append an optional trailing `[on {scope}]` group to a SOV event-handler
 * pattern when the schema declares a non-required `scope` role (S1 tabs-aria:
 * `set @attr to V on <scope>`). The i18n transformer appends `on <scope>` at the
 * clause end for SOV event-handler sets, so the scope is captured here. No-op for
 * commands without a scope role (e.g. put), keeping those patterns byte-identical.
 */
export function appendOptionalScope(
  tokens: PatternToken[],
  extraction: Record<string, ExtractionRule>,
  commandSchema: CommandSchema
): void {
  const scopeRole = commandSchema.roles.find(r => r.role === 'scope' && !r.required);
  if (!scopeRole) return;
  tokens.push({
    type: 'group',
    optional: true,
    tokens: [
      { type: 'literal', value: 'on' },
      { type: 'role', role: 'scope', optional: true, expectedTypes: ['selector', 'reference'] },
    ],
  });
  extraction.scope = { fromRole: 'scope' };
}

/**
 * Generate SOV event handler pattern (Japanese, Korean, Turkish).
 */
export function generateSOVEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker (after event in SOV)
  // Handle multi-word markers like Korean "할 때" by splitting into separate tokens
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      // Multi-word marker: create a token for each word
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      // Single-word marker: include alternatives
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // Optional destination with its marker
  const destMarker = profile.roleMarkers.destination;
  if (destMarker) {
    tokens.push({
      type: 'group',
      optional: true,
      tokens: [
        { type: 'role', role: 'destination', optional: true },
        destMarker.alternatives
          ? { type: 'literal', value: destMarker.primary, alternatives: destMarker.alternatives }
          : { type: 'literal', value: destMarker.primary },
      ],
    });
  }

  // Patient role
  tokens.push({ type: 'role', role: 'patient', optional: false });

  // Patient marker (postposition/particle after patient)
  const patientMarker = profile.roleMarkers.patient;
  if (patientMarker) {
    const patMarkerToken: PatternToken = patientMarker.alternatives
      ? { type: 'literal', value: patientMarker.primary, alternatives: patientMarker.alternatives }
      : { type: 'literal', value: patientMarker.primary };
    tokens.push(patMarkerToken);
  }

  // Command verb at end (SOV)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  // Optional trailing source phrase (post-verb, where the transformer
  // emits `remove X from Y`'s from-phrase in SOV output)
  tokens.push(...eventHandlerSourceGroup(commandSchema, profile.roleMarkers.source));
  tokens.push(...eventHandlerDestinationGroup(commandSchema, profile.roleMarkers.destination));

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov`,
    language: profile.code,
    command: 'on', // This is an event handler pattern
    priority: (config.basePriority ?? 100) + 50, // Higher priority than simple commands
    template: {
      format: `{event} ${eventMarker.primary} {destination?} {patient} ${patientMarker?.primary || ''} ${keyword.primary}`,
      tokens,
    },
    extraction: {
      action: { value: commandSchema.action }, // Extract the wrapped command
      event: { fromRole: 'event' },
      patient: { fromRole: 'patient' },
      ...eventHandlerDestinationExtraction(commandSchema),
      ...eventHandlerSourceExtraction(commandSchema),
    },
  };
}

/**
 * Generate patient-first SOV event handler pattern (simple, no destination).
 *
 * Alternative SOV ordering where the patient precedes the event:
 * - Bengali: .active কে ক্লিক এ টগল  (patient কে event এ verb)
 * - Quechua: .active ta ñitiy pi tikray  (patient ta event pi verb)
 *
 * No optional destination group to avoid greedy match issues when
 * destination and event markers collide (e.g., Bengali তে/এ).
 */
export function generateSOVPatientFirstEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Patient role first
  tokens.push({ type: 'role', role: 'patient', optional: false });

  // Patient marker (postposition after patient)
  const patientMarker = profile.roleMarkers.patient;
  if (patientMarker) {
    const patMarkerToken: PatternToken = patientMarker.alternatives
      ? { type: 'literal', value: patientMarker.primary, alternatives: patientMarker.alternatives }
      : { type: 'literal', value: patientMarker.primary };
    tokens.push(patMarkerToken);
  }

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker (after event in SOV)
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // Command verb at end (SOV)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  // Optional trailing source phrase (post-verb, where the transformer
  // emits `remove X from Y`'s from-phrase in SOV output)
  tokens.push(...eventHandlerSourceGroup(commandSchema, profile.roleMarkers.source));
  // Trailing destination phrase. `swap` is the two-object exception: its second
  // element is the WITH-marked patient content (`#b से` / `#b দিয়ে`), but the
  // patient-first flip binds the first element to `patient`, so the trailing
  // element must bind to `destination` — and the group therefore has to accept
  // the language's with-word, not only the locative dest-marker. Merge the
  // patient's with-word (schema markerOverride) into the dest-marker's
  // alternatives for swap; otherwise `#b <with>` never matches and #b drops
  // (hi/bn/tr/qu rendered the invalid `swap with #a`). Non-swap commands and
  // languages without a with-override are byte-identical to before.
  let trailingDestMarker = profile.roleMarkers.destination;
  if (commandSchema.action === 'swap' && trailingDestMarker) {
    const withWord = commandSchema.roles.find(r => r.role === 'patient')?.markerOverride?.[
      profile.code
    ];
    if (withWord && withWord !== trailingDestMarker.primary) {
      const existing = trailingDestMarker.alternatives ?? [];
      if (!existing.includes(withWord)) {
        trailingDestMarker = { ...trailingDestMarker, alternatives: [...existing, withWord] };
      }
    }
  }
  tokens.push(...eventHandlerDestinationGroup(commandSchema, trailingDestMarker));

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-patient-first`,
    language: profile.code,
    command: 'on',
    priority: (config.basePriority ?? 100) + 45,
    template: {
      format: `{patient} ${patientMarker?.primary || ''} {event} ${eventMarker.primary} ${keyword.primary}`,
      tokens,
    },
    extraction: {
      action: { value: commandSchema.action },
      event: { fromRole: 'event' },
      patient: { fromRole: 'patient' },
      ...eventHandlerDestinationExtraction(commandSchema),
      ...eventHandlerSourceExtraction(commandSchema),
    },
  };
}

/**
 * Generate patient-first SOV event handler pattern with required destination.
 *
 * For patterns like:
 * - Quechua: .highlight ta noqa man ñitiy pi yapay  (patient ta dest man event pi verb)
 *
 * Destination is REQUIRED (not optional) to avoid greedy match ambiguity.
 * Only generated when destination and event markers are different.
 */
export function generateSOVPatientFirstWithDestEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Patient role first
  tokens.push({ type: 'role', role: 'patient', optional: false });

  // Patient marker
  const patientMarker = profile.roleMarkers.patient;
  if (patientMarker) {
    const patMarkerToken: PatternToken = patientMarker.alternatives
      ? { type: 'literal', value: patientMarker.primary, alternatives: patientMarker.alternatives }
      : { type: 'literal', value: patientMarker.primary };
    tokens.push(patMarkerToken);
  }

  // Required destination with its marker
  const destMarker = profile.roleMarkers.destination;
  if (destMarker) {
    tokens.push({ type: 'role', role: 'destination', optional: false });
    tokens.push(
      destMarker.alternatives
        ? { type: 'literal', value: destMarker.primary, alternatives: destMarker.alternatives }
        : { type: 'literal', value: destMarker.primary }
    );
  }

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker (after event in SOV)
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // Command verb at end (SOV)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-patient-first-dest`,
    language: profile.code,
    command: 'on',
    priority: (config.basePriority ?? 100) + 40, // Lower than simple patient-first
    template: {
      format: `{patient} ${patientMarker?.primary || ''} {destination} ${destMarker?.primary || ''} {event} ${eventMarker.primary} ${keyword.primary}`,
      tokens,
    },
    extraction: {
      action: { value: commandSchema.action },
      event: { fromRole: 'event' },
      patient: { fromRole: 'patient' },
      destination: { fromRole: 'destination' },
    },
  };
}

/**
 * Generate SOV compact event handler pattern for languages with no-space forms.
 *
 * This handles Korean compact forms where the event marker is attached directly
 * to the event word without a space:
 * - 클릭할때 .active를토글 (click+when toggle .active)
 *
 * The pattern uses a single token for the no-space marker alternatives.
 */
export function generateSOVCompactEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker as single token (using no-space alternatives)
  // Filter alternatives to only include no-space versions
  const noSpaceAlternatives =
    eventMarker.alternatives?.filter(alt => !alt.includes(' ') && alt.length > 1) || [];

  if (noSpaceAlternatives.length > 0) {
    tokens.push({
      type: 'literal',
      value: noSpaceAlternatives[0],
      alternatives: noSpaceAlternatives.slice(1),
    });
  }

  // Optional destination with its marker
  const destMarker = profile.roleMarkers.destination;
  if (destMarker) {
    tokens.push({
      type: 'group',
      optional: true,
      tokens: [
        { type: 'role', role: 'destination', optional: true },
        destMarker.alternatives
          ? { type: 'literal', value: destMarker.primary, alternatives: destMarker.alternatives }
          : { type: 'literal', value: destMarker.primary },
      ],
    });
  }

  // Patient role
  tokens.push({ type: 'role', role: 'patient', optional: false });

  // Patient marker (postposition/particle after patient)
  const patientMarker = profile.roleMarkers.patient;
  if (patientMarker) {
    const patMarkerToken: PatternToken = patientMarker.alternatives
      ? { type: 'literal', value: patientMarker.primary, alternatives: patientMarker.alternatives }
      : { type: 'literal', value: patientMarker.primary };
    tokens.push(patMarkerToken);
  }

  // Command verb at end (SOV)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  // Optional trailing source phrase (post-verb, where the transformer
  // emits `remove X from Y`'s from-phrase in SOV output)
  tokens.push(...eventHandlerSourceGroup(commandSchema, profile.roleMarkers.source));
  tokens.push(...eventHandlerDestinationGroup(commandSchema, profile.roleMarkers.destination));

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-compact`,
    language: profile.code,
    command: 'on', // This is an event handler pattern
    priority: (config.basePriority ?? 100) + 52, // Slightly higher priority for compact forms
    template: {
      format: `{event}${noSpaceAlternatives[0] || ''} {patient} ${keyword.primary}`,
      tokens,
    },
    extraction: {
      action: { value: commandSchema.action }, // Extract the wrapped command
      event: { fromRole: 'event' },
      patient: { fromRole: 'patient' },
      ...eventHandlerDestinationExtraction(commandSchema),
      ...eventHandlerSourceExtraction(commandSchema),
    },
  };
}

/**
 * Generate SOV simple event handler pattern (patient optional, defaults to 'me').
 *
 * Supports patterns like:
 * - Japanese: クリック で 増加 (click on increment)
 * - Korean: 클릭 할 때 증가 (click when increment)
 * - Turkish: tıklama da artır (click on increment)
 *
 * The patient is not explicitly specified - it defaults to 'me' (current element).
 */
export function generateSOVSimpleEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker (after event in SOV)
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      // Multi-word marker: create a token for each word
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // Command verb at end (SOV) - no patient required
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-simple`,
    language: profile.code,
    command: 'on',
    priority: (config.basePriority ?? 100) + 48, // Lower than full pattern (50) but higher than base
    template: {
      format: `{event} ${eventMarker.primary} ${keyword.primary}`,
      tokens,
    },
    extraction: {
      action: { value: commandSchema.action },
      event: { fromRole: 'event' },
      patient: { default: { type: 'reference', value: 'me' } }, // Default to 'me'
    },
  };
}

/**
 * Generate SOV temporal event handler pattern.
 *
 * Supports patterns with temporal markers like:
 * - Japanese: クリック 時 .active を 切り替え (click time toggle .active)
 * - Japanese: クリック の 時 .active を 切り替え (click's time toggle .active)
 *
 * Uses profile.eventHandler.temporalMarkers for language-specific temporal words.
 */
export function generateSOVTemporalEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  config: GeneratorConfig
): LanguagePattern | null {
  const temporalMarkers = profile.eventHandler?.temporalMarkers;
  if (!temporalMarkers || temporalMarkers.length === 0) return null;

  const tokens: PatternToken[] = [];

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Optional possessive marker (の in Japanese)
  if (profile.possessive?.marker) {
    tokens.push({
      type: 'group',
      optional: true,
      tokens: [{ type: 'literal', value: profile.possessive.marker }],
    });
  }

  // Temporal marker (時, とき in Japanese)
  tokens.push({
    type: 'literal',
    value: temporalMarkers[0],
    alternatives: temporalMarkers.slice(1),
  });

  // Patient role
  tokens.push({ type: 'role', role: 'patient', optional: false });

  // Patient marker
  const patientMarker = profile.roleMarkers.patient;
  if (patientMarker?.primary) {
    const patMarkerToken: PatternToken = patientMarker.alternatives
      ? { type: 'literal', value: patientMarker.primary, alternatives: patientMarker.alternatives }
      : { type: 'literal', value: patientMarker.primary };
    tokens.push(patMarkerToken);
  }

  // Command verb at end
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-temporal`,
    language: profile.code,
    command: 'on',
    priority: (config.basePriority ?? 100) + 49, // Between simple and full pattern
    template: {
      format: `{event} ${temporalMarkers[0]} {patient} ${keyword.primary}`,
      tokens,
    },
    extraction: {
      action: { value: commandSchema.action },
      event: { fromRole: 'event' },
      patient: { fromRole: 'patient' },
    },
  };
}
/**
 * Generate SOV two-role event handler pattern (for put/set commands).
 *
 * Patterns:
 * - Japanese put: 入力 で "test" を #output に 入れる
 *   [event] [eventMarker] [patient] [patientMarker] [destination] [destMarker] [verb]
 * - Korean set: 변경 할 때 x 를 10 에 설정
 *   [event] [eventMarker] [role1] [role1Marker] [role2] [role2Marker] [verb]
 * - Turkish put: giriş de "test" i #output a koy
 *   [event] [eventMarker] [patient] [patientMarker] [destination] [destMarker] [verb]
 */
export function generateSOVTwoRoleEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker (after event in SOV)
  // Handle multi-word markers like Korean "할 때" by splitting into separate tokens
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      // Multi-word marker: create a token for each word
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      // Single-word marker: include alternatives
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // Get the two required roles from the schema
  const requiredRoles = commandSchema.roles.filter(r => r.required);

  // Sort by SOV position (lower number = earlier in sentence)
  const sortedRoles = [...requiredRoles].sort((a, b) => {
    const aPos = a.sovPosition ?? 999;
    const bPos = b.sovPosition ?? 999;
    return aPos - bPos;
  });

  // Add each role with its marker
  for (const roleSpec of sortedRoles) {
    // Add the role
    tokens.push({ type: 'role', role: roleSpec.role, optional: false });

    // Get marker for this role - markerOverride/profile default + markerVariants.
    const { marker, alternatives: markerAlternatives } = resolveRoleMarker(roleSpec, profile);

    // Add the marker token
    if (marker) {
      const markerToken: PatternToken = markerAlternatives
        ? { type: 'literal', value: marker, alternatives: markerAlternatives }
        : { type: 'literal', value: marker };
      tokens.push(markerToken);
    }
  }

  // Command verb at end (SOV)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  // Build format string
  const roleNames = sortedRoles.map(r => `{${r.role}}`).join(' ');

  const extraction: Record<string, ExtractionRule> = {
    action: { value: commandSchema.action }, // Extract the wrapped command
    event: { fromRole: 'event' },
    ...Object.fromEntries(sortedRoles.map(r => [r.role, { fromRole: r.role }])),
  };
  appendOptionalScope(tokens, extraction, commandSchema);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-2role`,
    language: profile.code,
    command: 'on', // This is an event handler pattern
    priority: (config.basePriority ?? 100) + 55, // Higher priority than single-role patterns
    template: {
      format: `{event} ${eventMarker.primary} ${roleNames} ${keyword.primary}`,
      tokens,
    },
    extraction,
  };
}

/**
 * Generate event-LAST SOV two-role event handler pattern.
 *
 * Some SOV emissions place the whole event phrase AFTER the command verb, as a
 * trailing afterthought:
 * - Japanese put: "Done!" を 私 に 置く クリック で
 *   [patient] [pMarker] [destination] [dMarker] [verb] [event] [eventMarker]
 *
 * The event-first (`{event} で {p} {d} 置く`) and dest-first
 * (`{p} {event} で 置く {d}`) variants both expect the event before the verb, so
 * this order fell to the bare command pattern (`put-ja-generated`) — which runs
 * immediately at execute() time, before the click is dispatched, so the effect
 * is invisible to the R2 execution snapshot (S4, ja put-content-basic).
 */
export function generateSOVTwoRoleEventLastEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  const requiredRoles = commandSchema.roles.filter(r => r.required);
  const sortedRoles = [...requiredRoles].sort((a, b) => {
    const aPos = a.sovPosition ?? 999;
    const bPos = b.sovPosition ?? 999;
    return aPos - bPos;
  });

  // Roles + their markers first (SOV position order)
  for (const roleSpec of sortedRoles) {
    tokens.push({ type: 'role', role: roleSpec.role, optional: false });
    const { marker, alternatives: markerAlternatives } = resolveRoleMarker(roleSpec, profile);
    if (marker) {
      tokens.push(
        markerAlternatives
          ? { type: 'literal', value: marker, alternatives: markerAlternatives }
          : { type: 'literal', value: marker }
      );
    }
  }

  // Command verb (SOV: after the roles)
  tokens.push(
    keyword.alternatives
      ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
      : { type: 'literal', value: keyword.primary }
  );

  // Trailing event phrase: {event} {eventMarker}
  tokens.push({ type: 'role', role: 'event', optional: false });
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      for (const word of markerWords) tokens.push({ type: 'literal', value: word });
    } else {
      tokens.push(
        eventMarker.alternatives
          ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
          : { type: 'literal', value: eventMarker.primary }
      );
    }
  }

  const roleNames = sortedRoles.map(r => `{${r.role}}`).join(' ');

  const extraction: Record<string, ExtractionRule> = {
    action: { value: commandSchema.action },
    event: { fromRole: 'event' },
    ...Object.fromEntries(sortedRoles.map(r => [r.role, { fromRole: r.role }])),
  };
  appendOptionalScope(tokens, extraction, commandSchema);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-2role-event-last`,
    language: profile.code,
    command: 'on',
    // Below the event-first / dest-first variants — this is the fallback order.
    priority: (config.basePriority ?? 100) + 40,
    template: {
      format: `${roleNames} ${keyword.primary} {event} ${eventMarker.primary}`,
      tokens,
    },
    extraction,
  };
}

/**
 * Generate destination-first SOV two-role event handler pattern.
 *
 * For languages where the roles precede the event in SOV order:
 * - Bengali: @disabled কে ক্লিক এ সেট সত্য তে
 *   [destination] [destMarker] [event] [eventMarker] [verb] [patient] [patientMarker]
 * - Bengali: "Hello" কে ক্লিক এ রাখুন #output তে
 *   [patient] [patientMarker] [event] [eventMarker] [verb] [destination] [destMarker]
 */
export function generateSOVTwoRoleDestFirstEventHandlerPattern(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  keyword: KeywordTranslation,
  eventMarker: RoleMarker,
  config: GeneratorConfig
): LanguagePattern {
  const tokens: PatternToken[] = [];

  // Get the two required roles from the schema
  const requiredRoles = commandSchema.roles.filter(r => r.required);
  const sortedRoles = [...requiredRoles].sort((a, b) => {
    const aPos = a.sovPosition ?? 999;
    const bPos = b.sovPosition ?? 999;
    return aPos - bPos;
  });

  // First role before event
  const firstRole = sortedRoles[0];
  tokens.push({ type: 'role', role: firstRole.role, optional: false });

  // First role marker (markerOverride/profile default + markerVariants allomorphs)
  const { marker: firstMarker, alternatives: firstMarkerAlts } = resolveRoleMarker(
    firstRole,
    profile
  );
  if (firstMarker) {
    const firstMarkerToken: PatternToken = firstMarkerAlts
      ? { type: 'literal', value: firstMarker, alternatives: firstMarkerAlts }
      : { type: 'literal', value: firstMarker };
    tokens.push(firstMarkerToken);
  }

  // Event role
  tokens.push({ type: 'role', role: 'event', optional: false });

  // Event marker
  if (eventMarker.position === 'after') {
    const markerWords = eventMarker.primary.split(/\s+/);
    if (markerWords.length > 1) {
      for (const word of markerWords) {
        tokens.push({ type: 'literal', value: word });
      }
    } else {
      const markerToken: PatternToken = eventMarker.alternatives
        ? { type: 'literal', value: eventMarker.primary, alternatives: eventMarker.alternatives }
        : { type: 'literal', value: eventMarker.primary };
      tokens.push(markerToken);
    }
  }

  // Command verb (between event and second role in this variant)
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };
  tokens.push(verbToken);

  // Second role after verb
  const secondRole = sortedRoles[1];
  tokens.push({ type: 'role', role: secondRole.role, optional: false });

  // Second role marker (markerOverride/profile default + markerVariants allomorphs)
  const { marker: secondMarker, alternatives: secondMarkerAlts } = resolveRoleMarker(
    secondRole,
    profile
  );
  if (secondMarker) {
    const markerToken: PatternToken = secondMarkerAlts
      ? { type: 'literal', value: secondMarker, alternatives: secondMarkerAlts }
      : { type: 'literal', value: secondMarker };
    tokens.push(markerToken);
  }

  const extraction: Record<string, ExtractionRule> = {
    action: { value: commandSchema.action },
    event: { fromRole: 'event' },
    [firstRole.role]: { fromRole: firstRole.role },
    [secondRole.role]: { fromRole: secondRole.role },
  };
  appendOptionalScope(tokens, extraction, commandSchema);

  return {
    id: `${commandSchema.action}-event-${profile.code}-sov-2role-dest-first`,
    language: profile.code,
    command: 'on',
    priority: (config.basePriority ?? 100) + 48, // Slightly lower than event-first two-role
    template: {
      format: `{${firstRole.role}} {event} ${eventMarker.primary} ${keyword.primary} {${secondRole.role}}`,
      tokens,
    },
    extraction,
  };
}
